import React, { useState, useEffect, useMemo } from 'react';
import { useNetworkContext } from '@/contexts/NetworkContext';
import { Serviceability, FileType } from '@/types/data';
import {
  DataGrid,
  TreeDataGrid,
  type Column,
  textEditor,
  type SortColumn,
  RenderGroupCellProps,
} from 'react-data-grid';
import { useServiceabilityData } from '@/hooks/useServiceabilityData';
// Using direct path references to SVG files in the public directory
const trashIcon = '/trash.svg';
const saveIcon = '/save.svg';
const infoIcon = '/info.svg';
import { EmptyState } from '@/components/common/EmptyState';
import { ServiceabilityHeader } from '@/components/RightPanel/ServiceabilityHeader';
import { AutosaveIndicator } from '@/components/common/AutosaveIndicator';

const ServiceabilityTab: React.FC = () => {
  const { currentNetwork } = useNetworkContext();
  const {
    serviceability,
    isEdited,
    isSaving,
    addEmptyRow,
    discardChanges,
    handleRowsChange,
    deleteRow,
  } = useServiceabilityData(currentNetwork);
  const [isLoading, setIsLoading] = useState(true);
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);

  // Custom renderGroupCell function to ensure group name and toggle are on the same line
  const renderGroupCell = ({
    groupKey,
    toggleGroup,
    isExpanded,
  }: RenderGroupCellProps<Serviceability>) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <button
          tabIndex={-1}
          onClick={toggleGroup}
          style={{ cursor: 'pointer', background: 'none', border: 'none', padding: '0 4px' }}
        >
          {isExpanded ? '▼' : '▶'}
        </button>
        <span>{String(groupKey)}</span>
      </div>
    );
  };

  // Grouping options
  const groupingOptions = ['courier', 'state', 'pickup_zone', 'dropoff_zone'];
  const [selectedGroupingOptions, setSelectedGroupingOptions] = useState<readonly string[]>([]);
  const [expandedGroupIds, setExpandedGroupIds] = useState<ReadonlySet<unknown>>(new Set());

  // Set loading state when network changes
  useEffect(() => {
    setIsLoading(true);
    // Short timeout to simulate loading and prevent flickering
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [currentNetwork]);

  // Get comparator function for sorting
  const getComparator = (
    sortColumn: string
  ): ((a: Serviceability, b: Serviceability) => number) => {
    // Special handling for dropoff and pickup which are 'y' or 'n'
    if (sortColumn === 'dropoff' || sortColumn === 'pickup') {
      return (a, b) => {
        const aValue = a[sortColumn as 'dropoff' | 'pickup'];
        const bValue = b[sortColumn as 'dropoff' | 'pickup'];
        // 'y' should come before 'n' in ascending order
        if (aValue === bValue) return 0;
        return aValue === 'y' ? -1 : 1;
      };
    }

    // For other string properties, use localeCompare
    return (a, b) => {
      return a[sortColumn as keyof Serviceability].localeCompare(
        b[sortColumn as keyof Serviceability]
      );
    };
  };

  // Apply sorting to rows
  const sortedRows = useMemo((): readonly Serviceability[] => {
    if (sortColumns.length === 0) return serviceability;

    return [...serviceability].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [serviceability, sortColumns]);

  // Function to group rows by column key
  function rowGrouper(rows: readonly Serviceability[], columnKey: string) {
    // @ts-expect-error

    return Object.groupBy(rows, (r) => r[columnKey]) as Record<string, readonly Serviceability[]>;
  }

  // Function to toggle grouping option
  function toggleGroupingOption(option: string, enabled: boolean) {
    const index = selectedGroupingOptions.indexOf(option);
    if (enabled) {
      if (index === -1) {
        setSelectedGroupingOptions((options) => [...options, option]);
      }
    } else if (index !== -1) {
      setSelectedGroupingOptions((options) => {
        return options.filter((_, i) => i !== index);
      });
    }
    setExpandedGroupIds(new Set());
  }

  // Define columns for the data grid
  const columns: Column<Serviceability>[] = [
    {
      key: 'courier',
      name: 'Courier',
      sortable: true,
      renderEditCell: textEditor,
      width: 120,
      resizable: true,
      renderGroupCell,
    },
    {
      key: 'dropoff',
      name: 'Dropoff',
      sortable: true,
      renderEditCell: textEditor,
      width: 80,
      resizable: true,
    },
    {
      key: 'pickup',
      name: 'Pickup',
      sortable: true,
      renderEditCell: textEditor,
      width: 80,
      resizable: true,
    },
    {
      key: 'suburb',
      name: 'Suburb',
      sortable: true,
      renderEditCell: textEditor,
      width: 150,
      resizable: true,
    },
    {
      key: 'postcode',
      name: 'Postal Code',
      sortable: true,
      renderEditCell: textEditor,
      width: 100,
      resizable: true,
    },
    {
      key: 'state',
      name: 'State',
      sortable: true,
      renderEditCell: textEditor,
      width: 100,
      resizable: true,
      renderGroupCell,
    },
    {
      key: 'dropoff_zone',
      name: 'Dropoff Zone',
      sortable: true,
      renderEditCell: textEditor,
      width: 120,
      resizable: true,
      renderGroupCell,
    },
    {
      key: 'pickup_zone',
      name: 'Pickup Zone',
      sortable: true,
      renderEditCell: textEditor,
      width: 120,
      resizable: true,
      renderGroupCell,
    },
    {
      key: 'delete',
      name: '',
      width: 50,
      renderCell: ({ rowIdx }) => (
        <button
          onClick={() => deleteRow(rowIdx)}
          className="text-red-600 hover:text-red-800 p-1 transition-colors"
          title="Delete this entry"
        >
          <img src={trashIcon} alt="Delete" className="h-5 w-5" />
        </button>
      ),
    },
  ];

  if (!currentNetwork) {
    return (
      <EmptyState
        message="No network selected"
        subMessage="Please select a network from the left panel to view and edit serviceability."
      />
    );
  }

  const serviceabilityFile = currentNetwork.files.find(
    (file) => file.type === FileType.Serviceability
  );

  if (!serviceabilityFile) {
    return (
      <EmptyState
        message="No serviceability file found"
        subMessage="The selected network does not contain a serviceability file. Please select a different network or add a serviceability file to this network."
      />
    );
  }

  if (isLoading) {
    return (
      <div className="py-4 flex justify-center items-center h-64">
        <div className="animate-pulse text-indigo-600">Loading serviceability data...</div>
      </div>
    );
  }

  return (
    <div className="py-4">
      <ServiceabilityHeader
        addEmptyRow={addEmptyRow}
        discardChanges={discardChanges}
        isEdited={isEdited}
      >
        <AutosaveIndicator isSaving={isSaving} isEdited={isEdited} />
      </ServiceabilityHeader>

      <div className="mb-4">
        <div className="flex items-center mb-2">
          <span className="font-medium mr-2">Group by:</span>
          <div className="flex gap-3">
            {groupingOptions.map((option) => (
              <label key={option} className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedGroupingOptions.includes(option)}
                  onChange={(event) => toggleGroupingOption(option, event.target.checked)}
                  className="rounded block w-5 h-5 mx-auto cursor-pointer focus:outline-2 focus:outline-offset-[-3px] focus:outline-[var(--rdg-checkbox-focus-color)]"
                />
                <span className="capitalize ml-1">{option}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="border rounded overflow-hidden shadow-sm">
          {selectedGroupingOptions.length > 0 ? (
            <TreeDataGrid
              columns={columns}
              rows={sortedRows}
              onRowsChange={handleRowsChange}
              className="rdg-light"
              style={{ height: 'calc(100vh - 280px)', minHeight: '400px' }}
              sortColumns={sortColumns}
              onSortColumnsChange={setSortColumns}
              groupBy={selectedGroupingOptions}
              rowGrouper={rowGrouper}
              expandedGroupIds={expandedGroupIds}
              onExpandedGroupIdsChange={setExpandedGroupIds}
            />
          ) : (
            <DataGrid
              columns={columns}
              rows={sortedRows}
              onRowsChange={handleRowsChange}
              className="rdg-light"
              style={{ height: 'calc(100vh - 280px)', minHeight: '400px' }}
              sortColumns={sortColumns}
              onSortColumnsChange={setSortColumns}
            />
          )}
        </div>
      </div>

      <div
        className={`text-sm p-2 rounded ${isEdited ? 'bg-indigo-50 text-indigo-700' : 'text-gray-500'}`}
      >
        {isEdited ? (
          <div className="flex items-center">
            <img src={saveIcon} alt="Save" className="h-5 w-5" />
            <span className="ml-1">
              Your changes are automatically saved to local storage and will be applied when you
              export the network
            </span>
          </div>
        ) : (
          <div className="flex items-center">
            <img src={infoIcon} alt="Info" className="h-5 w-5" />
            <span className="ml-1">Changes will be automatically saved as you edit</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceabilityTab;
